import React, { Key, Ref, useCallback, useEffect, useImperativeHandle, useState } from 'react';
import { Paging, Table, usePaging, useAsync, Form, Input, CustomFilter, EllipsisSpan } from '@maxtropy/components';
import { formatOptionData, getChildNodesByParentIds } from '@/shared/components/CascadingMultipleSelector/utils';
import { useQuery } from '@/utils/utils';
import { Device, DeviceListRequest, getDeviceList } from '@/api/device';
import { getDeviceTypeTree } from '@/api/deviceType';
import { DeviceSelectModalRef } from '../types';
import styles from './index.module.scss';
import { Cascader } from 'antd';
import { DefaultOptionType } from 'antd/es/cascader';

interface FilterParams {
  name?: string;
  typeId?: number[][];
}

type SearchParams = Omit<DeviceListRequest, 'page' | 'size' | 'gatewayId' | 'iotProtocol'>;

interface DeviceSelectModalProps {
  bindDeviceIds?: Key[];
  existingDeviceIds?: number[];
}

const normalizeKey = (key: Key): Key => {
  if (typeof key === 'string' && /^\d+$/.test(key)) return Number(key);
  return key;
};

const columns = () => [
  {
    title: '设备编号',
    dataIndex: 'code',
    ellipsis: { showTitle: true },
    render: (v: string) => <EllipsisSpan value={v} />,
  },
  {
    title: '设备名称',
    dataIndex: 'name',
    ellipsis: { showTitle: true },
    render: (v: string) => <EllipsisSpan value={v} />,
  },
  {
    title: '所属类目',
    dataIndex: 'typeName',
    ellipsis: { showTitle: true },
    render: (v: string) => <EllipsisSpan value={v ?? '--'} />,
  },
];

const DeviceSelectModal = React.forwardRef(
  ({ bindDeviceIds, existingDeviceIds }: DeviceSelectModalProps, ref: Ref<DeviceSelectModalRef>) => {
    const pagingInfo = usePaging();
    const [form] = Form.useForm();
    const { pageOffset, pageSize, setTotalCount, setPageOffset } = pagingInfo;
    const [searchParams, setSearchParams] = useState<SearchParams>();
    const [selectedRowKeys, setSelectedRowKeys] = useState<Key[]>([]);
    const [selectedRows, setSelectedRows] = useState<Device[]>([]);

    const deviceTypeData = useAsync(getDeviceTypeTree);

    const { data, isLoading }: { data?: Device[]; isLoading?: boolean } = useQuery(
      useCallback(async () => {
        if (!deviceTypeData) return;
        const res = await getDeviceList({
          ...searchParams,
          typeId: searchParams?.typeId ?? [],
          page: pageOffset,
          size: pageSize,
        });
        if (res) setTotalCount(res.total);
        return res.list;
      }, [pageOffset, pageSize, searchParams, setTotalCount, deviceTypeData])
    );

    useEffect(() => {
      setSelectedRowKeys((bindDeviceIds ?? []).map(normalizeKey));
      setSelectedRows([]);
    }, [bindDeviceIds]);

    useEffect(() => {
      const normalizedSelectedKeys = new Set((selectedRowKeys ?? []).map(key => Number(normalizeKey(key))));
      const pageSelectedRows = (data ?? []).filter(row => normalizedSelectedKeys.has(row.id));

      setSelectedRows(prev => {
        const validPrev = prev.filter(Boolean);
        const preservedRows = validPrev.filter(row => normalizedSelectedKeys.has(row.id));
        const existingIds = new Set(preservedRows.map(row => row.id));
        const mergedPageRows = pageSelectedRows.filter(row => !existingIds.has(row.id));
        return [...preservedRows, ...mergedPageRows];
      });
    }, [data, selectedRowKeys]);

    const onReset = () => {
      form.resetFields();
      setSearchParams(undefined);
      form.submit();
      setPageOffset(1);
    };

    const onFinish = (val: FilterParams) => {
      const typeId = getChildNodesByParentIds(val.typeId, deviceTypeData?.tree);
      const params: SearchParams = {
        codeOrName: val.name,
        typeId,
      };
      setSearchParams(params);
      setPageOffset(1);
    };

    const rowSelection = {
      preserveSelectedRowKeys: true,
      selectedRowKeys,
      onChange: (newSelectedRowKeys: Key[], newSelectedRows: Device[]) => {
        setSelectedRowKeys(newSelectedRowKeys);
        setSelectedRows(prev => {
          const validPrev = prev.filter(Boolean);
          const validNewRows = (newSelectedRows ?? []).filter(Boolean);
          const existingIds = new Set(validPrev.map(row => row.id));
          const newRows = validNewRows.filter(row => !existingIds.has(row.id));
          const newSelectedKeySet = new Set(newSelectedRowKeys.map(k => Number(k)));
          const keptRows = validPrev.filter(row => newSelectedKeySet.has(row.id));
          return [...keptRows, ...newRows];
        });
      },
      getCheckboxProps: (record: Device) => ({
        disabled: existingDeviceIds?.includes(record.id),
      }),
    };

    useImperativeHandle(ref, () => ({
      selectedRowKeys,
      selectedRows: selectedRows.filter(Boolean).map(r => ({
        id: 0, // 绑定记录 ID（新选择时使用 0）
        deviceId: r.id,
        name: r.name,
        code: r.code,
      })),
    }));

    const length = [...new Set([...(bindDeviceIds ?? []), ...(selectedRowKeys ?? [])])].length;
    const filter = (inputValue: string, path: DefaultOptionType[]) =>
      path.some(option => (option.label as string).toLowerCase().indexOf(inputValue.toLowerCase()) > -1);

    return (
      <div>
        <CustomFilter<FilterParams> form={form} onFinish={val => onFinish(val as FilterParams)} onReset={onReset}>
          <Form.Item name="name" label="编号/名称">
            <Input placeholder="请输入编号或名称查询" style={{ width: 160 }} />
          </Form.Item>

          <Form.Item name="typeId" label="所属类目">
            <Cascader
              options={formatOptionData(deviceTypeData)}
              allowClear={false}
              fieldNames={{ children: 'child' }}
              multiple
              maxTagCount="responsive"
              showSearch={{ filter }}
              placeholder={'请选择所属类目'}
            />
          </Form.Item>
        </CustomFilter>

        <Table
          scroll={{ y: 300 }}
          rowKey="id"
          loading={isLoading}
          rowSelection={rowSelection}
          columns={columns()}
          dataSource={data}
          bottomLeftExtra={
            <div className={styles.tips}>
              <span>已选择</span>
              <span className={styles.red}>{length}</span>
              <span>项</span>
            </div>
          }
        >
          <Paging pagingInfo={pagingInfo} />
        </Table>
      </div>
    );
  }
);

export default DeviceSelectModal;
